import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Plus, Users } from "@digital-twins/design-system/icons";
import type { CreativeSubject } from "@digital-twins/shared-types";
import type { SubjectManagerProps } from "@/types/creative-library";
import { Button } from "@/components/ui/button";
import { Text } from "@/components/ui/Text";
import { CreativeEditorDialog } from "./CreativeEditorDialog";
import { SubjectEditor } from "./SubjectEditor";
import { PrivateImage } from "./PrivateImage";

export function SubjectManager({
  brand,
  subjects,
  creatives,
  onClose,
  onSaved,
  onUse,
  initialSubjectToken,
}: SubjectManagerProps) {
  const { t } = useTranslation("app");
  const [active, setActive] = useState<CreativeSubject | null | undefined>(
    undefined,
  );
  const brandToken = brand?.token;
  const resolvedRequest = useRef<string | null>(null);
  useEffect(() => {
    const key = `${brandToken ?? ""}:${initialSubjectToken ?? ""}`;
    if (resolvedRequest.current === key) return;
    const requested = initialSubjectToken
      ? subjects.find(
          (item) =>
            item.token === initialSubjectToken &&
            item.brandToken === brandToken,
        )
      : undefined;
    if (brandToken && initialSubjectToken && !requested) return;
    resolvedRequest.current = key;
    setActive(requested);
  }, [brandToken, initialSubjectToken, subjects]);
  if (brand && active !== undefined)
    return (
      <SubjectEditor
        key={active?.token ?? "new"}
        brand={brand}
        subject={active}
        creatives={creatives}
        onClose={initialSubjectToken ? onClose : () => setActive(undefined)}
        onSaved={onSaved}
        onUse={onUse}
      />
    );
  return (
    <CreativeEditorDialog
      open={Boolean(brand)}
      busy={false}
      onClose={onClose}
      kind="subjects"
      title={t("creativeLibrary.subjects")}
      description={brand?.name ?? ""}
      testId="creative-subjects-dialog"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            {t("creativeLibrary.done")}
          </Button>
          {brand?.canEdit && (
            <Button
              variant="primary"
              onClick={() => setActive(null)}
              data-testid="subject-new"
            >
              <Plus className="h-4 w-4" />
              {t("creativeLibrary.newSubject")}
            </Button>
          )}
        </>
      }
    >
      <Text size="sm" color="muted">
        {t("creativeLibrary.subjectsHint")}
      </Text>
      {subjects
        .filter((item) => item.brandToken === brand?.token)
        .map((item) => (
          <button
            key={item.token}
            onClick={() => setActive(item)}
            className="flex items-center gap-3 rounded-xl border border-border bg-background p-3 text-left"
            data-testid={`subject-open-${item.token}`}
          >
            <PrivateImage
              source={
                item.primaryReferenceUnavailable
                  ? undefined
                  : creatives.find((image) =>
                      item.primaryReferenceToken
                        ? image.token === item.primaryReferenceToken &&
                          item.referenceTokens.includes(image.token)
                        : item.referenceTokens.includes(image.token),
                    )?.mediaUrl
              }
              alt={item.name}
              className="h-16 w-16 shrink-0 rounded-lg object-contain"
            />
            <span className="flex min-w-0 flex-col gap-1">
              <Text className="truncate">{item.name}</Text>
              <Text size="xs" color="muted">
                {t(`creativeLibrary.subjectKinds.${item.kind}`)}
              </Text>
            </span>
          </button>
        ))}
      {!subjects.some((item) => item.brandToken === brand?.token) && (
        <div className="flex items-center gap-3 rounded-xl border border-dashed border-border p-4">
          <Users className="h-5 w-5 shrink-0 text-muted-foreground" />
          <Text color="muted" size="sm">
            {t("creativeLibrary.noSubjects")}
          </Text>
        </div>
      )}
    </CreativeEditorDialog>
  );
}
